import * as Joi from 'joi';
import {
  getEmailSchema,
  getJoiObject,
  getMasterCodeObjectDropdownSchema,
  getNricFinJoiObject,
  getPhoneNumberSchema,
  validateDate,
  validateDobBasedOnNric,
  validateFloor,
  validateHouseBlockNo,
  validatePostalCode,
  validateSingaporeCountry,
  validateStreetName,
} from '../../../utils/JoiUtils';
import { IExamMasterCodeItem, LookupListApiItem, LookupListItem } from '../../../api/user/model';
import { IdTypeType, preferredAddressType, preferredContactType } from '../../../models';

const countryCodeKey = 'Country code';
const telephoneKey = 'Telephone No.';

const getNextOfKinContactNumField = (fieldName: string | undefined) =>
  fieldName ?? 'Emergency Contact No.';
const getNextOfKinNameField = (fieldName: string | undefined) =>
  fieldName ?? 'Emergency Contact Name';

const getNextOfKinContactNoJoiObject = (
  nextOfKinContactNumField: string | undefined,
  disableValidation = false
) =>
  getJoiObject(
    Joi.when('nextOfKinCountryCode', {
      is: validateSingaporeCountry,
      then: getPhoneNumberSchema(
        false,
        getNextOfKinContactNumField(nextOfKinContactNumField),
        8,
        8
      ),
      otherwise: getPhoneNumberSchema(false, getNextOfKinContactNumField(nextOfKinContactNumField)),
    }),
    disableValidation
  );

export interface CreateEditCandidateSchemaData {
  name?: string;
  IdType?: IdTypeType;
  NricFin?: string;
  hanyupinyinName?: string;
  gender?: IExamMasterCodeItem;
  dateOfBirth?: string;
  email?: string;
  race?: IExamMasterCodeItem;
  secondaryRace?: string;
  nationality?: IExamMasterCodeItem;
  prStatus?: IExamMasterCodeItem;
  nationalityIssueDate?: string;
  citizenship?: IExamMasterCodeItem;
  occupation?: LookupListApiItem;
  school?: LookupListItem;

  // addresses
  houseOrBlkNo?: string;
  streetName?: string;
  building?: string;
  floor?: string;
  unit?: string;
  postalCode?: string;
  foreignAddress1?: string;
  foreignAddress2?: string;
  foreignAddress3?: string;
  preferredAddress?: preferredAddressType;

  // contact numbers
  telephoneCountryCode?: IExamMasterCodeItem;
  telephoneContactNumber?: string;
  mobileCountryCode?: IExamMasterCodeItem;
  mobileContactNumber?: string;
  preferredTelephoneOrMobile?: preferredContactType;

  // next of kin
  nextOfKinName?: string;
  nextOfKinCountryCode?: IExamMasterCodeItem;
  nextOfKinContactNumber?: string;
  nextOfKinRelationshipToCandidate?: IExamMasterCodeItem;

  action?: LookupListApiItem;
  approver?: LookupListApiItem;
  remarks?: string;
}

export type CandidateProfileResponseIsDisableInputs = {
  [P in keyof CreateEditCandidateSchemaData]?: boolean;
};

const getNextOfKinNameJoiObject = (
  nextOfKinNameField: string | undefined,
  disableValidation = false
) =>
  getJoiObject(
    Joi.string()
      .required()
      .max(66)
      .required()
      .messages({
        'any.required': `${getNextOfKinNameField(nextOfKinNameField)} is not allowed to be empty`,
        'string.empty': `${getNextOfKinNameField(nextOfKinNameField)} is not allowed to be empty`,
        'string.max': `${getNextOfKinNameField(nextOfKinNameField)} is too long`,
      }),
    disableValidation
  );
const CreateEditCandidateSchema = (
  isDisableInputs?: CandidateProfileResponseIsDisableInputs,
  nextOfKinNameField?: string,
  nextOfKinContactNumField?: string
) =>
  Joi.object<CreateEditCandidateSchemaData>({
    name: getJoiObject(
      Joi.string().required().max(66).messages({
        'any.required': 'Name is not allowed to be empty',
        'string.empty': 'Name is not allowed to be empty',
        'string.max': 'Name is too long',
      }),
      isDisableInputs?.name
    ),
    IdType: getJoiObject(
      Joi.string().required().messages({
        'any.required': 'ID Type is not allowed to be empty.',
        'string.base': 'ID Type is not allowed to be empty.',
      }),
      isDisableInputs?.IdType
    ),
    NricFin: getNricFinJoiObject(isDisableInputs?.NricFin),
    dateOfBirth: getJoiObject(
      Joi.string()
        .required()
        .custom((value, helpers) => validateDobBasedOnNric(value, helpers))
        .messages({
          'any.required': 'Date of birth is not allowed to be empty',
          'string.empty': 'Date of birth is not allowed to be empty',
          'string.pattern.nricYear':
            'Invalid NRIC format/NRIC format does not match with Year of Birth',
        }),
      isDisableInputs?.dateOfBirth
    ),
    school: getJoiObject(
      Joi.object().required().messages({
        'any.required': 'Please select school',
        'object.base': 'Please select school',
      }),
      isDisableInputs?.school
    ),
    hanyupinyinName: getJoiObject(Joi.string().allow(''), isDisableInputs?.hanyupinyinName),
    gender: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'Gender'),
      isDisableInputs?.gender
    ),
    race: getJoiObject(getMasterCodeObjectDropdownSchema(false, 'Race'), isDisableInputs?.race),
    citizenship: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'Citizenship'),
      isDisableInputs?.citizenship
    ),
    nationality: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'Nationality'),
      isDisableInputs?.nationality
    ),
    nationalityIssueDate: getJoiObject(
      Joi.custom((...joiParams) => validateDate(true, ...joiParams)).messages({
        'date.pattern.notValid': 'Nationality issue date must be a valid date',
        'date.pattern.isAfter': 'Nationality issue date cannot be of future dates',
      }),
      isDisableInputs?.nationalityIssueDate
    ),
    prStatus: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'PR status'),
      isDisableInputs?.prStatus
    ),
    occupation: getJoiObject(
      getMasterCodeObjectDropdownSchema(true, 'occupation'),
      isDisableInputs?.occupation
    ),
    email: getJoiObject(getEmailSchema('Email'), isDisableInputs?.email),
    mobileCountryCode: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, countryCodeKey),
      isDisableInputs?.mobileCountryCode
    ),
    mobileContactNumber: getJoiObject(
      Joi.when('mobileCountryCode', {
        is: validateSingaporeCountry,
        then: getPhoneNumberSchema(false, 'Mobile No.', 8, 8),
        otherwise: getPhoneNumberSchema(false, 'Mobile No.'),
      }),
      isDisableInputs?.mobileContactNumber
    ),
    telephoneCountryCode: getJoiObject(
      Joi.when('preferredTelephoneOrMobile', {
        is: 'T',
        then: getMasterCodeObjectDropdownSchema(false, countryCodeKey),
        otherwise: Joi.when('telephoneContactNumber', {
          is: Joi.string().exist(),
          then: getMasterCodeObjectDropdownSchema(false, countryCodeKey),
          otherwise: getMasterCodeObjectDropdownSchema(true, countryCodeKey),
        }),
      }),
      isDisableInputs?.telephoneCountryCode
    ),
    telephoneContactNumber: getJoiObject(
      Joi.when('telephoneCountryCode', {
        is: validateSingaporeCountry,
        then: Joi.when('preferredTelephoneOrMobile', {
          is: 'T',
          then: getPhoneNumberSchema(false, telephoneKey, 8, 8),
          otherwise: getPhoneNumberSchema(true, telephoneKey, 8, 8),
        }),
        otherwise: Joi.when('preferredTelephoneOrMobile', {
          is: 'T',
          then: getPhoneNumberSchema(false, telephoneKey),
          otherwise: getPhoneNumberSchema(true, telephoneKey),
        }),
      }),
      isDisableInputs?.telephoneContactNumber
    ),
    preferredTelephoneOrMobile: getJoiObject(
      Joi.required(),
      isDisableInputs?.preferredTelephoneOrMobile
    ),
    houseOrBlkNo: getJoiObject(
      Joi.string()
        .max(10)
        .required()
        .when('postalCode', {
          is: Joi.string().not('000000'),
          then: Joi.custom(validateHouseBlockNo),
        })
        .messages({
          'any.required': 'House/Block No. is not allowed to be empty.',
          'string.empty': 'House/Block No. is not allowed to be empty.',
          'string.max': 'House/Block No. is too long.',
          'string.pattern.houseBlockNo': 'First character must be number.',
        }),
      isDisableInputs?.houseOrBlkNo
    ),
    streetName: getJoiObject(
      Joi.string().max(32).required().custom(validateStreetName).messages({
        'any.required': 'Street Name is not allowed to be empty',
        'string.empty': 'Street Name is not allowed to be empty',
        'string.max': 'Street Name is too long',
        'string.pattern.streetName': 'First character must be alphabet letter.',
      }),
      isDisableInputs?.streetName
    ),
    building: getJoiObject(
      Joi.string().max(66).allow('', null).messages({
        'string.max': 'Building is too long',
      }),
      isDisableInputs?.building
    ),
    floor: getJoiObject(
      Joi.string().max(2).allow('', null).custom(validateFloor).messages({
        'string.max': 'Floor is too long',
        'string.pattern.floor': 'Please enter number.',
      }),
      isDisableInputs?.floor
    ),
    unit: getJoiObject(
      Joi.string().max(5).allow('', null).messages({
        'string.max': 'Unit is too long',
        'string.pattern.unit': 'Please enter number and/or alphabet letter.',
      }),
      isDisableInputs?.unit
    ),
    postalCode: getJoiObject(
      Joi.string().max(6).required().custom(validatePostalCode).messages({
        'any.required': 'Postal Code is not allowed to be empty',
        'string.empty': 'Postal Code is not allowed to be empty',
        'string.max': 'Postal Code is too long',
        'string.pattern.postalCode': 'Please enter 6 digits.',
      }),
      isDisableInputs?.postalCode
    ),
    preferredAddress: getJoiObject(Joi.required(), isDisableInputs?.preferredAddress),
    foreignAddress1: getJoiObject(
      Joi.when('preferredAddress', {
        is: 'F',
        then: Joi.string().required().max(60).messages({
          'string.empty':
            'Foreign Address 1 is not allowed to be empty when foreign address is the preferred address.',
          'any.required':
            'Foreign Address 1 is not allowed to be empty when foreign address is the preferred address.',
          'string.max': 'Foreign Address 1 is too long',
        }),
        otherwise: Joi.string().allow('').max(60).messages({
          'string.max': 'Foreign Address 1 is too long',
        }),
      }),
      isDisableInputs?.foreignAddress1
    ),
    foreignAddress2: getJoiObject(
      Joi.string().allow('').max(60).messages({
        'string.max': 'Foreign Address 2 is too long',
      }),
      isDisableInputs?.foreignAddress2
    ),
    foreignAddress3: getJoiObject(
      Joi.string().allow('').max(60).messages({
        'string.max': 'Foreign Address 3 is too long',
      }),
      isDisableInputs?.foreignAddress3
    ),
    nextOfKinName: getNextOfKinNameJoiObject(nextOfKinNameField, isDisableInputs?.nextOfKinName),
    nextOfKinCountryCode: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, countryCodeKey),
      isDisableInputs?.nextOfKinCountryCode
    ),
    nextOfKinContactNumber: getNextOfKinContactNoJoiObject(
      nextOfKinContactNumField,
      isDisableInputs?.nextOfKinContactNumber
    ),
    nextOfKinRelationshipToCandidate: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'Relationship to candidate'),
      isDisableInputs?.nextOfKinRelationshipToCandidate
    ),
    action: getJoiObject(
      Joi.required().messages({
        'any.required': 'Action is required',
      }),
      isDisableInputs?.action
    ),
    approver: getJoiObject(
      Joi.any().required().messages({
        'any.required': 'Please select Approver.',
      }),
      isDisableInputs?.approver
    ),
    remarks: getJoiObject(
      Joi.string().required().trim().min(1).messages({}),
      isDisableInputs?.remarks
    ),
  });
export default CreateEditCandidateSchema;
